/**
 * Operator activity phases (3 only — shared with EAD-agent activity_format.py):
 * Initializing → Running → Run complete
 *
 * Periodic report generation / delivery during a run stays in Running.
 */

export const OPERATOR_ACTIVITY_PHASES = [
  "Initializing",
  "Running",
  "Run complete",
] as const;

export type OperatorActivityPhase = (typeof OPERATOR_ACTIVITY_PHASES)[number];

const PHASE_PREFIX_RE =
  /^\[(Initializing|Running|Run complete|Complete|Done|Generating report|Generting report|Saving and delivering Report)\]\s*/i;

export function stripOperatorActivityPhasePrefix(raw: string): string {
  return String(raw || "").replace(PHASE_PREFIX_RE, "").trim();
}

const PHASE_COLORS: Record<OperatorActivityPhase, string> = {
  Initializing: "#8b8d93",
  Running: "#6ea8fe",
  "Run complete": "#7bc96f",
};

export function normalizeOperatorPhase(raw: string): OperatorActivityPhase | "" {
  const key = String(raw || "").trim().toLowerCase();
  if (!key) return "";
  if (key === "initializing") return "Initializing";
  if (key === "running") return "Running";
  if (
    key === "run complete" ||
    key === "complete" ||
    key === "completed" ||
    key === "done"
  ) {
    return "Run complete";
  }
  // Legacy 4-phase tags → Running (in-run report/delivery work)
  if (
    key === "generating report" ||
    key === "generting report" ||
    key === "saving and delivering report" ||
    key === "saving and delivering" ||
    key === "reporting" ||
    key === "delivering report"
  ) {
    return "Running";
  }
  for (const phase of OPERATOR_ACTIVITY_PHASES) {
    if (key === phase.toLowerCase()) return phase;
  }
  return "";
}

export function parseOperatorActivityTitle(raw: string): {
  phase: OperatorActivityPhase | "";
  title: string;
} {
  const text = String(raw || "").trim();
  const match = PHASE_PREFIX_RE.exec(text);
  if (!match) return { phase: "", title: text };
  const phase = normalizeOperatorPhase(match[1]);
  return { phase, title: text.slice(match[0].length).trim() };
}

export function operatorPhaseColor(phase: OperatorActivityPhase | ""): string {
  if (!phase) return "var(--muted)";
  return PHASE_COLORS[phase] ?? "var(--muted)";
}

/** Map activity phase tag to the header chip label. */
export function operatorPhaseToStatusLabel(phase: OperatorActivityPhase | ""): string | null {
  switch (phase) {
    case "Initializing":
      return "Initializing...";
    case "Running":
      return "Running...";
    case "Run complete":
      return "Done";
    default:
      return null;
  }
}

/** Remove inline Found/Next/Why labels from stored or legacy activity text. */
export function stripActivityPropertyLabels(text: string): string {
  return String(text || "")
    .replace(/\s*[—–-]\s*\bfound:\s*/gi, " ")
    .replace(/\s*[—–-]\s*\bnext:\s*/gi, ". ")
    .replace(/\s*[—–-]\s*\b(?:reasoning|why):\s*/gi, ". ")
    .replace(/\bfound:\s*/gi, "")
    .replace(/\bnext:\s*/gi, "")
    .replace(/\b(?:reasoning|why):\s*/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
const FOUND_RE = /\bfound:\s*/i;
const NEXT_RE = /\bnext:\s*/i;
const WHY_RE = /\b(?:reasoning|why):\s*/i;

/** Split headline body into Found / Next when encoded in title or separate fields. */
export function parseOperatorActivityFields(
  rawTitle: string,
  opts?: { finding?: string; nextDirection?: string; reasoning?: string },
): {
  phase: OperatorActivityPhase | "";
  headline: string;
  finding: string;
  nextDirection: string;
  reasoning: string;
} {
  const { phase, title } = parseOperatorActivityTitle(rawTitle);
  let headline = title;
  let finding = String(opts?.finding || "").trim();
  let nextDirection = String(opts?.nextDirection || "").trim();
  let reasoning = String(opts?.reasoning || "").trim();

  if (headline && FOUND_RE.test(headline)) {
    const parts = headline.split(NEXT_RE);
    const foundPart = parts[0].replace(FOUND_RE, "").replace(/[—–-]\s*$/, "").trim();
    if (!finding && foundPart) finding = foundPart;
    if (!nextDirection && parts.length > 1) {
      nextDirection = parts.slice(1).join(" ").trim();
    }
    headline = headline
      .replace(FOUND_RE, "")
      .replace(NEXT_RE, "")
      .replace(WHY_RE, "")
      .replace(/[—–-]\s*$/, "")
      .trim();
  }

  if (!reasoning && WHY_RE.test(nextDirection)) {
    const parts = nextDirection.split(WHY_RE);
    const pre = parts[0].trim();
    const post = parts.slice(1).join(" ").trim();
    if (post) reasoning = post;
    nextDirection = pre;
  }

  if (!reasoning && WHY_RE.test(headline)) {
    const parts = headline.split(WHY_RE);
    const pre = parts[0].trim();
    const post = parts.slice(1).join(" ").trim();
    if (post) reasoning = post;
    headline = pre;
  }

  return { phase, headline, finding, nextDirection, reasoning };
}

function normalizeActivitySentence(text: string): string {
  const trimmed = String(text || "").trim().replace(/\s+/g, " ");
  if (!trimmed) return "";
  if (/[.!?…]$/.test(trimmed)) return trimmed;
  return `${trimmed}.`;
}

function activityTextContainedIn(needle: string, haystack: string): boolean {
  const n = needle.trim().toLowerCase();
  const h = haystack.trim().toLowerCase();
  if (!n || !h) return false;
  if (h.includes(n) || n.includes(h)) return true;
  const nWords = n.split(/\s+/).filter(Boolean);
  if (nWords.length < 4) return false;
  const prefix = nWords.slice(0, 4).join(" ");
  return h.includes(prefix);
}

/** Merge structured activity fields into one or two readable paragraphs (no Found/Next/Why labels). */
export function formatConversationalActivityProse(
  rawTitle: string,
  opts?: { finding?: string; nextDirection?: string; reasoning?: string },
): { phase: OperatorActivityPhase | ""; paragraphs: string[] } {
  const { phase, headline, finding, nextDirection, reasoning } = parseOperatorActivityFields(
    rawTitle,
    opts,
  );

  if (
    headline.includes("\n\n") &&
    !finding &&
    !nextDirection &&
    !reasoning &&
    !opts?.finding &&
    !opts?.nextDirection &&
    !opts?.reasoning
  ) {
    const paragraphs = headline
      .split(/\n\n+/)
      .map((part) => part.replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .map((part) => normalizeActivitySentence(part));
    if (paragraphs.length) return { phase, paragraphs };
  }

  const primary: string[] = [];
  let head = headline.trim().replace(/[—–-]\s*$/, "").trim();

  if (head && finding && activityTextContainedIn(finding, head)) {
    primary.push(normalizeActivitySentence(head));
  } else {
    if (head && !(finding && activityTextContainedIn(head, finding))) {
      primary.push(normalizeActivitySentence(head));
    }
    if (finding && !primary.some((part) => activityTextContainedIn(finding, part))) {
      primary.push(normalizeActivitySentence(finding));
    }
  }

  if (
    nextDirection &&
    !primary.some((part) => activityTextContainedIn(nextDirection, part))
  ) {
    primary.push(normalizeActivitySentence(nextDirection));
  }

  const paragraphOne = primary.join(" ").replace(/\s+/g, " ").trim();
  const paragraphs = paragraphOne ? [paragraphOne] : [];

  const reason = String(reasoning || "").trim();
  if (reason && !paragraphOne.toLowerCase().includes(reason.toLowerCase().slice(0, Math.min(36, reason.length)))) {
    paragraphs.push(normalizeActivitySentence(reason));
  }

  if (!paragraphs.length) {
    const fallback = stripActivityPropertyLabels(String(rawTitle || "").trim());
    if (fallback) paragraphs.push(fallback);
  }

  return {
    phase,
    paragraphs: paragraphs.map((part) => stripActivityPropertyLabels(part)).filter(Boolean),
  };
}

export function formatOperatorActivityLine(
  rawTitle: string,
  opts?: { finding?: string; nextDirection?: string; reasoning?: string },
): { phase: OperatorActivityPhase | ""; display: string } {
  const { phase, paragraphs } = formatConversationalActivityProse(rawTitle, opts);
  const body = paragraphs.join("\n\n");
  const display = phase ? `[${phase}] ${body}`.trim() : body;
  return { phase, display: display || rawTitle };
}
